import React, { useRef } from 'react';
import { formatMeasurement } from '../../utils/measurements';

/**
 * PDFMeasurementRenderer - SVG overlay for rendering PDF measurements
 */
export const PDFMeasurementRenderer = ({
  measurements,
  selectedIds = [],
  drawingMeasurement,
  scale,
  offset,
  units = 'imperial',
  onMeasurementClick,
  onGripStart,
  onGripEnd,
  onGripMove,
  onCanvasClick,
  onCanvasMove,
  onWheel,
  isDraggingGrip = false,
  isDrawingMeasurement = false,
  isAnnotationCloudToolActive = false,
  isDrawingAnnotationCloud = false,
  onAnnotationClick,
  onAnnotationMove,
  onAnnotationDoubleClick,
}) => {
  // Ref to track if a grip was just clicked (to prevent SVG handler from firing)
  const gripClickedRef = useRef(false);

  // Convert world coordinates to screen coordinates
  const toScreen = (point) => ({
    x: point.x * scale + offset.x,
    y: point.y * scale + offset.y,
  });

  // Format area in sq ft
  const formatArea = (sqInches) => {
    const sqFeet = sqInches / 144;
    if (sqFeet < 1) {
      return `${(sqFeet * 144).toFixed(0)} sq in`;
    }
    return `${sqFeet.toLocaleString(undefined, { maximumFractionDigits: 1 })} SF`;
  };

  // Calculate centroid of polygon
  const getCentroid = (points) => {
    const n = points.length;
    let cx = 0, cy = 0;
    points.forEach(p => {
      cx += p.x;
      cy += p.y;
    });
    return { x: cx / n, y: cy / n };
  };

  // Calculate midpoint
  const getMidpoint = (p1, p2) => ({
    x: (p1.x + p2.x) / 2,
    y: (p1.y + p2.y) / 2,
  });

  // Get angle for text rotation
  const getAngle = (p1, p2) => {
    const dx = p2.x - p1.x;
    const dy = p2.y - p1.y;
    let angle = Math.atan2(dy, dx) * (180 / Math.PI);
    // Keep text readable (not upside down)
    if (angle > 90 || angle < -90) {
      angle += 180;
    }
    return angle;
  };

  const renderLinearMeasurement = (measurement, isSelected) => {
    const { id, points, length } = measurement;
    if (points.length < 2) return null;

    const p1Screen = toScreen(points[0]);
    const p2Screen = toScreen(points[1]);
    const midScreen = getMidpoint(p1Screen, p2Screen);
    const angle = getAngle(p1Screen, p2Screen);

    const color = isSelected ? '#ffaa00' : '#00c8ff';
    const strokeWidth = isSelected ? 3 : 2;
    const gripRadius = isSelected ? 8 : 5;

    return (
      <g
        key={id}
        onClick={(e) => {
          e.stopPropagation();
          onMeasurementClick?.(id, e.shiftKey);
        }}
        style={{ cursor: 'pointer' }}
      >
        {/* Main line */}
        <line
          x1={p1Screen.x}
          y1={p1Screen.y}
          x2={p2Screen.x}
          y2={p2Screen.y}
          stroke={color}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
        />

        {/* End markers / grips */}
        <circle
          cx={p1Screen.x}
          cy={p1Screen.y}
          r={gripRadius}
          fill={color}
          stroke={isSelected ? '#fff' : 'none'}
          strokeWidth={isSelected ? 2 : 0}
          style={{ cursor: isSelected ? 'move' : 'pointer', touchAction: 'none' }}
          onPointerDown={(e) => {
            if (isSelected && onGripStart) {
              e.stopPropagation();
              e.preventDefault();
              gripClickedRef.current = true;
              onGripStart(id, 0, e);
            }
          }}
          onPointerUp={(e) => {
            if (isDraggingGrip && onGripEnd) {
              e.stopPropagation();
              onGripEnd();
            }
          }}
        />
        <circle
          cx={p2Screen.x}
          cy={p2Screen.y}
          r={gripRadius}
          fill={color}
          stroke={isSelected ? '#fff' : 'none'}
          strokeWidth={isSelected ? 2 : 0}
          style={{ cursor: isSelected ? 'move' : 'pointer', touchAction: 'none' }}
          onPointerDown={(e) => {
            if (isSelected && onGripStart) {
              e.stopPropagation();
              e.preventDefault();
              gripClickedRef.current = true;
              onGripStart(id, 1, e);
            }
          }}
          onPointerUp={(e) => {
            if (isDraggingGrip && onGripEnd) {
              e.stopPropagation();
              onGripEnd();
            }
          }}
        />

        {/* Distance label background */}
        <rect
          x={midScreen.x - 35}
          y={midScreen.y - 10}
          width={70}
          height={20}
          rx={4}
          fill="rgba(12,18,24,0.9)"
          stroke={color}
          strokeWidth={1}
          transform={`rotate(${angle}, ${midScreen.x}, ${midScreen.y})`}
        />

        {/* Distance label */}
        <text
          x={midScreen.x}
          y={midScreen.y + 4}
          textAnchor="middle"
          fill={color}
          fontSize="11"
          fontWeight="bold"
          fontFamily="system-ui, sans-serif"
          transform={`rotate(${angle}, ${midScreen.x}, ${midScreen.y})`}
        >
          {formatMeasurement(length / 12, units)}
        </text>
      </g>
    );
  };

  const renderAreaMeasurement = (measurement, isSelected) => {
    const { id, points, area } = measurement;
    if (points.length < 3) return null;

    const screenPoints = points.map(toScreen);
    const pathData = screenPoints.map((p, i) =>
      `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`
    ).join(' ') + ' Z';

    const centroid = toScreen(getCentroid(points));
    const color = isSelected ? '#ffaa00' : '#00ffaa';
    const fillColor = isSelected ? 'rgba(255,170,0,0.15)' : 'rgba(0,255,170,0.15)';
    const gripRadius = isSelected ? 7 : 4;

    return (
      <g
        key={id}
        onClick={(e) => {
          e.stopPropagation();
          onMeasurementClick?.(id, e.shiftKey);
        }}
        style={{ cursor: 'pointer' }}
      >
        {/* Fill */}
        <path
          d={pathData}
          fill={fillColor}
          stroke={color}
          strokeWidth={isSelected ? 3 : 2}
          strokeLinejoin="round"
        />

        {/* Vertex markers / grips */}
        {screenPoints.map((p, i) => (
          <circle
            key={i}
            cx={p.x}
            cy={p.y}
            r={gripRadius}
            fill={color}
            stroke={isSelected ? '#fff' : 'none'}
            strokeWidth={isSelected ? 2 : 0}
            style={{ cursor: isSelected ? 'move' : 'pointer', touchAction: 'none' }}
            onPointerDown={(e) => {
              if (isSelected && onGripStart) {
                e.stopPropagation();
                e.preventDefault();
                gripClickedRef.current = true;
                onGripStart(id, i, e);
              }
            }}
            onPointerUp={(e) => {
              if (isDraggingGrip && onGripEnd) {
                e.stopPropagation();
                onGripEnd();
              }
            }}
          />
        ))}

        {/* Area label background */}
        <rect
          x={centroid.x - 40}
          y={centroid.y - 12}
          width={80}
          height={24}
          rx={4}
          fill="rgba(12,18,24,0.9)"
          stroke={color}
          strokeWidth={1}
        />

        {/* Area label */}
        <text
          x={centroid.x}
          y={centroid.y + 5}
          textAnchor="middle"
          fill={color}
          fontSize="12"
          fontWeight="bold"
          fontFamily="system-ui, sans-serif"
        >
          {formatArea(area)}
        </text>
      </g>
    );
  };

  const renderDrawingMeasurement = () => {
    if (!drawingMeasurement) return null;

    const { type, points, previewPoint } = drawingMeasurement;

    if (type === 'linear') {
      if (points.length === 0) return null;

      const p1Screen = toScreen(points[0]);
      const p2Screen = previewPoint ? toScreen(previewPoint) : p1Screen;

      if (points.length === 1 && previewPoint) {
        return (
          <g>
            <line
              x1={p1Screen.x}
              y1={p1Screen.y}
              x2={p2Screen.x}
              y2={p2Screen.y}
              stroke="#ff00ff"
              strokeWidth={2}
              strokeDasharray="8,4"
            />
            <circle cx={p1Screen.x} cy={p1Screen.y} r={6} fill="#ff00ff" />
            <circle cx={p2Screen.x} cy={p2Screen.y} r={6} fill="#ff00ff" fillOpacity={0.5} />
          </g>
        );
      }
    }

    if (type === 'area') {
      if (points.length === 0) return null;

      const screenPoints = points.map(toScreen);
      const allPoints = previewPoint
        ? [...screenPoints, toScreen(previewPoint)]
        : screenPoints;

      const pathData = allPoints.map((p, i) =>
        `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`
      ).join(' ');

      // Check if close to first point (to show closing preview)
      let isClosing = false;
      if (previewPoint && points.length >= 3) {
        const firstScreen = screenPoints[0];
        const previewScreen = toScreen(previewPoint);
        const dist = Math.sqrt(
          Math.pow(previewScreen.x - firstScreen.x, 2) +
          Math.pow(previewScreen.y - firstScreen.y, 2)
        );
        isClosing = dist < 20;
      }

      return (
        <g>
          {/* Path */}
          <path
            d={pathData + (isClosing ? ' Z' : '')}
            fill={isClosing ? 'rgba(255,0,255,0.15)' : 'none'}
            stroke="#ff00ff"
            strokeWidth={2}
            strokeDasharray={isClosing ? 'none' : '8,4'}
            strokeLinejoin="round"
          />

          {/* Vertex markers */}
          {screenPoints.map((p, i) => (
            <circle
              key={i}
              cx={p.x}
              cy={p.y}
              r={i === 0 && isClosing ? 10 : 6}
              fill="#ff00ff"
              fillOpacity={i === 0 && isClosing ? 0.5 : 1}
              stroke={i === 0 && isClosing ? '#ff00ff' : 'none'}
              strokeWidth={2}
            />
          ))}

          {/* Preview point */}
          {previewPoint && !isClosing && (
            <circle
              cx={toScreen(previewPoint).x}
              cy={toScreen(previewPoint).y}
              r={6}
              fill="#ff00ff"
              fillOpacity={0.5}
            />
          )}

          {/* Instructions */}
          {points.length < 3 && (
            <text
              x={screenPoints[screenPoints.length - 1]?.x + 15 || 100}
              y={screenPoints[screenPoints.length - 1]?.y - 10 || 100}
              fill="#ff00ff"
              fontSize="11"
              fontFamily="system-ui, sans-serif"
            >
              {points.length === 1 ? 'Click to add points' : 'Click near start to close'}
            </text>
          )}

          {isClosing && (
            <text
              x={screenPoints[0].x}
              y={screenPoints[0].y - 20}
              textAnchor="middle"
              fill="#ff00ff"
              fontSize="12"
              fontWeight="bold"
              fontFamily="system-ui, sans-serif"
            >
              Click to close
            </text>
          )}
        </g>
      );
    }

    return null;
  };

  // Enable pointer events when drawing measurements OR when annotation cloud tool is active
  const needsPointerEvents = isDraggingGrip || isDrawingMeasurement || isAnnotationCloudToolActive || isDrawingAnnotationCloud;

  return (
    <svg
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        pointerEvents: needsPointerEvents ? 'auto' : 'none',
        zIndex: 50,
      }}
      onWheel={onWheel}
      onPointerDown={(e) => {
        // Don't forward if a grip was clicked
        if (gripClickedRef.current) {
          gripClickedRef.current = false;
          return;
        }
        // When annotation cloud tool is active, forward clicks to annotation handler
        if ((isAnnotationCloudToolActive || isDrawingAnnotationCloud) && onAnnotationClick) {
          onAnnotationClick(e);
          return;
        }
        // When drawing a measurement, forward clicks to the canvas click handler
        if (isDrawingMeasurement && onCanvasClick) {
          onCanvasClick(e);
        }
      }}
      onPointerMove={(e) => {
        // Forward grip move when dragging
        if (isDraggingGrip && onGripMove) {
          onGripMove(e);
          return;
        }
        // Forward pointer move for annotation cloud preview
        if ((isAnnotationCloudToolActive || isDrawingAnnotationCloud) && onAnnotationMove) {
          onAnnotationMove(e);
          return;
        }
        // Forward pointer move for preview updates
        if (isDrawingMeasurement && onCanvasMove) {
          onCanvasMove(e);
        }
      }}
      onPointerUp={(e) => {
        if (isDraggingGrip && onGripEnd) {
          onGripEnd();
        }
      }}
      onDoubleClick={(e) => {
        // Forward double-click for annotation cloud closing
        if ((isAnnotationCloudToolActive || isDrawingAnnotationCloud) && onAnnotationDoubleClick) {
          onAnnotationDoubleClick(e);
        }
      }}
    >
      <g style={{ pointerEvents: 'visiblePainted' }}>
        {/* Render completed measurements */}
        {measurements.map((m) => {
          const isSelected = selectedIds.includes(m.id);
          if (m.type === 'linear') {
            return renderLinearMeasurement(m, isSelected);
          }
          if (m.type === 'area') {
            return renderAreaMeasurement(m, isSelected);
          }
          return null;
        })}

        {/* Render in-progress measurement */}
        {renderDrawingMeasurement()}
      </g>
    </svg>
  );
};

export default PDFMeasurementRenderer;
